import {
  attachIME,
  attachKeyboard,
  attachPointer,
  attachTouch,
  type IMEHandle,
  type WireEnvelope,
} from '@no-vnc-web/input';

import {
  HandoffMirror,
  sendUserCancel,
  sendUserDone,
  StepUpError,
  verifyTOTP,
  type HandoffState,
  type HandoffView,
} from '@no-vnc-web/rtc';

import { connectCloud, connectDebug, type ConnectHandle } from './rtc';

// ───────────────────────── DOM bindings ─────────────────────────

const $statusPill = must<HTMLElement>('#status-pill');
const $statusLabel = must<HTMLElement>('#status-label');
const $targetTask = must<HTMLElement>('#target-task');

const $btnComplete = must<HTMLButtonElement>('#btn-complete');
const $btnCancel = must<HTMLButtonElement>('#btn-cancel');
const $btnRefresh = must<HTMLButtonElement>('#btn-refresh');
const $btnFullscreen = must<HTMLButtonElement>('#btn-fullscreen');
const $btnDebug = must<HTMLButtonElement>('#btn-debug');

const $viewport = must<HTMLElement>('#viewport');
const $video = must<HTMLVideoElement>('#remote');
const $idlePlaceholder = must<HTMLElement>('#idle-placeholder');
const $idleSub = must<HTMLElement>('#idle-sub');
const $btnDevToggle = must<HTMLButtonElement>('#btn-dev-toggle');

const $debugPanel = must<HTMLElement>('#debug-panel');
const $logBody = must<HTMLElement>('#log-body');
const $logMeta = must<HTMLElement>('#log-meta');
const $logTags = document.querySelectorAll<HTMLButtonElement>('.nv-log-tag');

const $statLatency = must<HTMLElement>('#stat-latency');
const $statFps = must<HTMLElement>('#stat-fps');
const $statBitrate = must<HTMLElement>('#stat-bitrate');
const $statCodec = must<HTMLElement>('#stat-codec');
const $statScale = must<HTMLElement>('#stat-scale');
const $sparklineLatency = must<HTMLElement>('#sparkline-latency');

const $metaSession = must<HTMLElement>('#meta-session');
const $metaTask = must<HTMLElement>('#meta-task');
const $metaState = must<HTMLElement>('#meta-state');
const $metaRelay = must<HTMLElement>('#meta-relay');
const $metaRes = must<HTMLElement>('#meta-res');

const $modalBack = must<HTMLElement>('#modal-back');
const $modalTitle = must<HTMLElement>('#modal-title');
const $modalBody = must<HTMLElement>('#modal-body');
const $modalYes = must<HTMLButtonElement>('#modal-yes');
const $modalNo = must<HTMLButtonElement>('#modal-no');

const $devDrawer = must<HTMLElement>('#dev-drawer');
const $btnDevClose = must<HTMLButtonElement>('#btn-dev-close');

// Dev controls (existing)
const $endpoint = must<HTMLInputElement>('#endpoint');
const $connect = must<HTMLButtonElement>('#connect');
const $disconnect = must<HTMLButtonElement>('#disconnect');

const $sigBase = must<HTMLInputElement>('#sig-base');
const $sigWS = must<HTMLInputElement>('#sig-ws');
const $sigToken = must<HTMLInputElement>('#sig-token');
const $sigTOTP = must<HTMLInputElement>('#sig-totp');
const $sigConnect = must<HTMLButtonElement>('#sig-connect');

const $btnPasskey = must<HTMLButtonElement>('#btn-passkey');
const $passkeyTarget = must<HTMLInputElement>('#passkey-target');
const $passkeyResult = must<HTMLElement>('#passkey-result');
const $passkeyPhoneURL = must<HTMLAnchorElement>('#passkey-phone-url');
const $btnPasskeyCopy = must<HTMLButtonElement>('#btn-passkey-copy');
const $passkeyTTL = must<HTMLElement>('#passkey-ttl');

// ───────────────────────── Video bootstrap ─────────────────────────

$video.style.cursor = 'crosshair';
$video.setAttribute('tabindex', '0');
$video.muted = true;
$video.playsInline = true;
$video.autoplay = true;
$video.addEventListener('loadedmetadata', () => {
  log('info', `video meta: ${$video.videoWidth}x${$video.videoHeight}`);
  $metaRes.textContent = `${$video.videoWidth}×${$video.videoHeight}`;
});
$video.addEventListener('playing', () => {
  log('ok', 'video playing');
  setIdle(false);
});
$video.addEventListener('stalled', () => log('warn', 'video stalled'));
$video.addEventListener('suspend', () => log('info', 'video suspend'));
$video.addEventListener('error', () => {
  const err = $video.error;
  log('err', `video error: code=${err?.code ?? '?'} ${err?.message ?? ''}`);
});

// ───────────────────────── Session state ─────────────────────────

interface Session {
  handle: ConnectHandle;
  detachers: Array<() => void>;
  ime: IMEHandle;
  statsTimer?: number;
}

// 必须早于 mirror.subscribe(renderHandoff):subscribe 会同步回调一次初始视图。
let current: Session | null = null;

const mirror = new HandoffMirror();
mirror.subscribe(renderHandoff);

// ───────────────────────── UI wiring ─────────────────────────

$btnComplete.addEventListener('click', () => openModal('complete'));
$btnCancel.addEventListener('click', () => openModal('cancel'));

$btnRefresh.addEventListener('click', () => {
  if (!current) return;
  const pr = $video.play();
  if (pr && typeof pr.then === 'function') {
    pr.catch((e) => log('warn', `video.play() rejected: ${toMsg(e)}`));
  }
  log('info', 'manual video refresh');
});

$btnFullscreen.addEventListener('click', () => {
  if (!document.fullscreenElement) {
    void $viewport.requestFullscreen?.().catch((e) =>
      log('warn', `fullscreen: ${toMsg(e)}`),
    );
  } else {
    void document.exitFullscreen?.();
  }
});

$btnDebug.addEventListener('click', () => {
  const on = $btnDebug.classList.toggle('on');
  $debugPanel.hidden = !on;
});

$logTags.forEach((tag) => {
  tag.addEventListener('click', () => {
    $logTags.forEach((t) => t.classList.toggle('on', t === tag));
    logFilter = tag.dataset.filter ?? 'all';
    rerenderLog();
  });
});

$modalNo.addEventListener('click', closeModal);
$modalBack.addEventListener('click', (e) => {
  if (e.target === $modalBack) closeModal();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !$modalBack.hidden) closeModal();
});

$btnDevToggle.addEventListener('click', () => {
  $devDrawer.hidden = false;
});
$btnDevClose.addEventListener('click', () => {
  $devDrawer.hidden = true;
});

$connect.addEventListener('click', () => void onConnect());
$disconnect.addEventListener('click', onDisconnect);
$sigConnect.addEventListener('click', () => void onCloudConnect());

$btnPasskey.addEventListener('click', () => void onPasskeyStart());
$btnPasskeyCopy.addEventListener('click', () => {
  if (!$passkeyPhoneURL.href) return;
  void navigator.clipboard.writeText($passkeyPhoneURL.href);
  log('ok', 'passkey url copied to clipboard');
});

// Task chip — read from URL ?task=<text>, else fallback default.
const initialTask =
  new URLSearchParams(location.search).get('task') ?? '远程介入待中';
$targetTask.textContent = initialTask;
$metaTask.textContent = initialTask;

// ───────────────────────── Status pill ─────────────────────────

interface PillSpec {
  state: 'active' | 'handoff' | 'cancelled' | 'idle';
  label: string;
}

function pillFor(s: HandoffState): PillSpec {
  switch (s) {
    case 'HANDOFF_REQUESTED':
    case 'AWAITING_USER':
    case 'USER_CONTROLLING':
      return { state: 'active', label: 'AGENT 运行中' };
    case 'USER_COMPLETED':
    case 'VALIDATING':
    case 'AGENT_RESUMED':
      return { state: 'handoff', label: '已交回 AGENT' };
    case 'HANDOFF_FAILED':
      return { state: 'cancelled', label: '已取消' };
    case 'AGENT_RUNNING':
    default:
      return { state: current ? 'active' : 'idle', label: 'AGENT 运行中' };
  }
}

function renderHandoff(view: HandoffView): void {
  const spec = pillFor(view.current);
  // No data-state → default green active styling. Other states map to grey/red variants.
  if (spec.state === 'active') {
    $statusPill.removeAttribute('data-state');
  } else {
    $statusPill.setAttribute('data-state', spec.state);
  }
  $statusLabel.textContent = spec.label;
  $metaState.textContent = view.current;

  $btnComplete.disabled = !view.canComplete || !current;
  $btnCancel.disabled = !view.canCancel || !current;
  $btnPasskey.disabled = !current;

  if (view.sessionId) $metaSession.textContent = view.sessionId.slice(0, 16) + '…';
  if (view.taskId) {
    const taskLabel = view.taskId;
    $targetTask.textContent = taskLabel;
    $metaTask.textContent = taskLabel;
  }
  if (view.failureReason) {
    log('warn', `handoff reason: ${view.failureReason}`);
  }
}

// ───────────────────────── Modal ─────────────────────────

let modalKind: 'complete' | 'cancel' | null = null;

const MODAL_TEXTS = {
  complete: {
    title: '完成本次操作员介入？',
    body: '会上报「operator-assisted」事件并把控制权交还给 Agent。Agent 将基于当前页面状态继续任务。',
    yes: '确认完成',
    yesClass: 'nv-btn ok',
  },
  cancel: {
    title: '取消本次任务？',
    body: '此操作将立即终止 Agent 当前任务并标记为 cancelled。已产生的产出会保留。',
    yes: '确认取消',
    yesClass: 'nv-btn danger-ghost',
  },
} as const;

function openModal(kind: 'complete' | 'cancel'): void {
  if (!current) return;
  const t = MODAL_TEXTS[kind];
  modalKind = kind;
  $modalTitle.textContent = t.title;
  $modalBody.textContent = t.body;
  $modalYes.textContent = t.yes;
  $modalYes.className = t.yesClass;
  $modalBack.hidden = false;
}

function closeModal(): void {
  $modalBack.hidden = true;
  modalKind = null;
}

$modalYes.addEventListener('click', () => {
  if (!current || !modalKind) {
    closeModal();
    return;
  }
  const sid = mirror.view().sessionId;
  if (modalKind === 'complete') {
    sendUserDone(current.handle.send, sid);
    log('info', 'user_done sent');
  } else {
    sendUserCancel(current.handle.send, sid, 'user_cancel');
    log('info', 'user_cancel sent');
  }
  closeModal();
});

// ───────────────────────── Connect / disconnect ─────────────────────────

async function onConnect(): Promise<void> {
  if (current) {
    log('warn', 'already connected');
    return;
  }
  $connect.disabled = true;

  try {
    const handle = await connectDebug({
      endpoint: $endpoint.value.trim() || '/debug/offer',
      onTrack: attachTrack,
      onState: handlePcState,
      onLog: log,
      onMessage: routeMessage,
    });

    attachInputPipeline(handle, 'debug');
  } catch (e) {
    log('err', `connect failed: ${toMsg(e)}`);
    $connect.disabled = false;
  }
}

function onDisconnect(): void {
  if (current) {
    if (current.statsTimer !== undefined) window.clearInterval(current.statsTimer);
    for (const d of current.detachers) d();
    current.ime.stop();
    current.handle.close();
    current = null;
  }
  lastInbound = null;
  $video.srcObject = null;
  $connect.disabled = false;
  $disconnect.disabled = true;
  setIdle(true, '连接已断开');
  renderHandoff(mirror.view());
}

function attachTrack(stream: MediaStream): void {
  $video.srcObject = stream;
  const pr = $video.play();
  if (pr && typeof pr.then === 'function') {
    pr.catch((e) => log('warn', `video.play() rejected: ${toMsg(e)}`));
  }
  setIdle(false);
}

function handlePcState(state: RTCPeerConnectionState): void {
  log('info', `state → ${state}`);
  if (state === 'connected') log('ok', 'webrtc connected');
  if (state === 'failed' || state === 'disconnected' || state === 'closed') {
    onDisconnect();
  }
}

function routeMessage(env: WireEnvelope): void {
  mirror.handleEnvelope(env);
}

function attachInputPipeline(handle: ConnectHandle, mode: 'debug' | 'cloud'): void {
  const ime = attachIME(handle.send);
  const focusIME = () => ime.focusHidden();
  $video.addEventListener('pointerdown', focusIME);
  $video.addEventListener('touchstart', focusIME, { passive: true });

  const detachers: Array<() => void> = [
    attachPointer($video, handle.send),
    attachTouch($video, handle.send),
    attachKeyboard(document, handle.send),
    () => {
      $video.removeEventListener('pointerdown', focusIME);
      $video.removeEventListener('touchstart', focusIME);
    },
  ];

  const statsTimer = window.setInterval(() => {
    void sampleInboundStats(handle.pc);
  }, 1000);

  current = { handle, detachers, ime, statsTimer };
  $disconnect.disabled = false;
  $devDrawer.hidden = true;
  log('ok', `${mode} input pipeline attached`);
  renderHandoff(mirror.view());
}

// ───────────────────────── Cloud connect ─────────────────────────

async function onCloudConnect(): Promise<void> {
  const base = $sigBase.value.trim();
  const wsURL = $sigWS.value.trim();
  const token = $sigToken.value.trim();
  const totp = $sigTOTP.value.trim();
  if (!base || !wsURL || !token) {
    log('err', 'cloud: base/ws/token required');
    return;
  }

  $sigConnect.disabled = true;
  try {
    if (totp.length > 0) {
      log('info', 'cloud: verifying TOTP ...');
      await verifyTOTP(base, token, totp);
      log('ok', 'cloud: step-up passed');
    } else {
      log('warn', 'cloud: skipping TOTP (step-up disabled)');
    }
    await attachCloudSession(base, wsURL, token);
  } catch (e) {
    if (e instanceof StepUpError) {
      log('err', `cloud step-up failed (${e.code}): ${e.message}`);
    } else {
      log('err', `cloud connect: ${toMsg(e)}`);
    }
  } finally {
    $sigConnect.disabled = false;
  }
}

async function attachCloudSession(
  base: string,
  wsURL: string,
  token: string,
): Promise<void> {
  if (current) {
    log('warn', 'already connected');
    return;
  }

  // best-effort: surface relay host in the debug panel
  try {
    const u = new URL(wsURL);
    $metaRelay.textContent = u.host;
  } catch {
    // ignore
  }

  const handle = await connectCloud({
    wsURL,
    token,
    httpBase: base,
    onTrack: attachTrack,
    onState: (state) => {
      log('info', `cloud state → ${state}`);
      if (state === 'connected') log('ok', 'cloud webrtc connected');
      if (state === 'failed' || state === 'disconnected' || state === 'closed') {
        onDisconnect();
      }
    },
    onLog: log,
    onMessage: routeMessage,
  });

  attachInputPipeline(handle, 'cloud');
}

// ───────────────────────── Passkey ─────────────────────────

async function onPasskeyStart(): Promise<void> {
  const sigBase = ($sigBase.value.trim() || 'http://127.0.0.1:9444').replace(
    /\/$/,
    '',
  );
  const token = $sigToken.value.trim();
  const target = $passkeyTarget.value.trim();
  if (!token) {
    log('err', 'passkey: 需要先在 cloud-mode 填 user token');
    return;
  }
  $btnPasskey.disabled = true;
  try {
    const res = await fetch(sigBase + '/api/v1/passkey/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`HTTP ${res.status}: ${t}`);
    }
    const body = (await res.json()) as {
      handoff_id: string;
      phone_url: string;
      expires_at_unix: number;
    };
    const url = target
      ? body.phone_url + '?target=' + encodeURIComponent(target)
      : body.phone_url;
    $passkeyPhoneURL.href = url;
    $passkeyPhoneURL.textContent = url;
    const ttlMin = Math.max(
      0,
      Math.round((body.expires_at_unix - Date.now() / 1000) / 60),
    );
    $passkeyTTL.textContent = `在 ${ttlMin} 分钟内完成。handoff_id: ${body.handoff_id.slice(
      0,
      12,
    )}…`;
    $passkeyResult.hidden = false;
    log('ok', `passkey handoff created: ${body.handoff_id.slice(0, 12)}…`);
  } catch (e) {
    log('err', 'passkey start: ' + toMsg(e));
  } finally {
    $btnPasskey.disabled = !current;
  }
}

// ───────────────────────── Stats sampling ─────────────────────────

interface InboundSnapshot {
  ts: number;
  bytesReceived: number;
  framesDecoded: number;
  framesReceived: number;
  pliCount: number;
}
let lastInbound: InboundSnapshot | null = null;
const latencySeries: number[] = [];

async function sampleInboundStats(pc: RTCPeerConnection): Promise<void> {
  try {
    const stats = await pc.getStats();
    let inbound: (Record<string, unknown> & { type: string; kind?: string }) | null = null;
    let candidatePair: Record<string, unknown> | null = null;
    let codecId: string | undefined;
    const codecs = new Map<string, Record<string, unknown>>();
    stats.forEach((report) => {
      const r = report as Record<string, unknown> & {
        id?: string;
        type: string;
        kind?: string;
      };
      if (r.type === 'inbound-rtp' && r.kind === 'video') {
        inbound = r;
        if (typeof r['codecId'] === 'string') codecId = r['codecId'] as string;
      }
      if (
        r.type === 'candidate-pair' &&
        (r['nominated'] === true || r['state'] === 'succeeded')
      ) {
        candidatePair = r;
      }
      if (r.type === 'codec' && typeof r.id === 'string') {
        codecs.set(r.id, r);
      }
    });

    if (codecId && codecs.has(codecId)) {
      const c = codecs.get(codecId)!;
      const mime = typeof c['mimeType'] === 'string' ? (c['mimeType'] as string) : '';
      $statCodec.textContent = mime.replace(/^video\//i, '') || '—';
    }

    if (candidatePair) {
      const rtt = numField(candidatePair, 'currentRoundTripTime');
      if (rtt > 0) {
        const ms = Math.round(rtt * 1000);
        latencySeries.push(ms);
        if (latencySeries.length > 24) latencySeries.shift();
        renderLatency(ms);
      }
    }

    if (!inbound) return;
    const r = inbound as Record<string, unknown>;
    const snap: InboundSnapshot = {
      ts: Date.now(),
      bytesReceived: numField(r, 'bytesReceived'),
      framesDecoded: numField(r, 'framesDecoded'),
      framesReceived: numField(r, 'framesReceived'),
      pliCount: numField(r, 'pliCount'),
    };
    if (lastInbound) {
      const dt = (snap.ts - lastInbound.ts) / 1000;
      const kbps = Math.round(
        ((snap.bytesReceived - lastInbound.bytesReceived) * 8) / dt / 1000,
      );
      const fps = Math.round(
        (snap.framesDecoded - lastInbound.framesDecoded) / dt,
      );
      $statBitrate.innerHTML = `${kbps}<span class="nv-stat-unit">kbps</span>`;
      $statFps.innerHTML = `${fps}<span class="nv-stat-unit">/30</span>`;
      $statFps.classList.toggle('warn', fps === 0 || fps < 8);
      log(
        fps > 0 ? 'ok' : 'warn',
        `stats: ${kbps}kbps rx, ${fps}fps decoded, rxFrames=${snap.framesReceived}, pli=${snap.pliCount}`,
      );
      // scale (rendered / source)
      if ($video.videoWidth > 0) {
        const renderW = $video.clientWidth;
        const scale = Math.round((renderW / $video.videoWidth) * 100);
        $statScale.innerHTML = `${scale}<span class="nv-stat-unit">%</span>`;
      }
    }
    lastInbound = snap;
  } catch (e) {
    log('warn', `getStats: ${toMsg(e)}`);
  }
}

function renderLatency(ms: number): void {
  const kind = ms > 100 ? 'err' : ms > 50 ? 'warn' : 'ok';
  $statLatency.className = `nv-stat-v ${kind}`;
  $statLatency.innerHTML = `${ms}<span class="nv-stat-unit">ms</span>`;
  // sparkline
  const max = Math.max(...latencySeries, 1);
  $sparklineLatency.innerHTML = latencySeries
    .map(
      (v, i) =>
        `<i style="height:${Math.max(
          4,
          (v / max) * 18,
        )}px;opacity:${0.4 + (i / latencySeries.length) * 0.6}"></i>`,
    )
    .join('');
}

function numField(r: Record<string, unknown>, key: string): number {
  const v = r[key];
  return typeof v === 'number' ? v : 0;
}

// ───────────────────────── Log panel ─────────────────────────

type LogLevel = 'info' | 'ok' | 'warn' | 'err';
interface LogLine {
  ts: string;
  level: LogLevel;
  text: string;
}

const MAX_LOG_LINES = 400;
const logLines: LogLine[] = [];
let logFilter = 'all';

function log(level: LogLevel, msg: string): void {
  const ts = new Date().toISOString().slice(11, 23);
  logLines.push({ ts, level, text: msg });
  if (logLines.length > MAX_LOG_LINES) {
    logLines.splice(0, logLines.length - MAX_LOG_LINES);
  }
  appendLogLine(logLines[logLines.length - 1]);
}

function appendLogLine(line: LogLine): void {
  if (!matchesFilter(line)) {
    updateLogMeta();
    return;
  }
  const el = document.createElement('div');
  el.className = `nv-log-line ${cssLevel(line.level)}`;
  el.innerHTML = `<span class="nv-log-ts">[${line.ts}]</span><span></span>`;
  el.querySelector<HTMLElement>('span:last-child')!.textContent = line.text;
  $logBody.appendChild(el);
  while ($logBody.children.length > MAX_LOG_LINES) {
    $logBody.removeChild($logBody.children[0]);
  }
  $logBody.scrollTop = $logBody.scrollHeight;
  updateLogMeta();
}

function cssLevel(level: LogLevel): string {
  if (level === 'ok') return '';
  if (level === 'warn') return 'warn';
  if (level === 'err') return 'err';
  return 'info';
}

function matchesFilter(line: LogLine): boolean {
  if (logFilter === 'all') return true;
  const isStats = /^stats:/.test(line.text);
  if (logFilter === 'stats') return isStats;
  if (logFilter === 'events') return !isStats;
  return true;
}

function rerenderLog(): void {
  $logBody.innerHTML = '';
  for (const line of logLines) appendLogLine(line);
}

function updateLogMeta(): void {
  $logMeta.textContent = `${logLines.length} 行 · auto-scroll`;
}

// ───────────────────────── Idle placeholder ─────────────────────────

function setIdle(idle: boolean, msg?: string): void {
  $idlePlaceholder.hidden = !idle;
  $viewport.classList.toggle('is-idle', idle);
  if (idle && msg) $idleSub.textContent = msg;
}

// ───────────────────────── URL-driven bootstrap ─────────────────────────

async function bootstrapFromURL(): Promise<boolean> {
  const m = /^\/join\/([^/]+)\/?$/.exec(location.pathname);
  if (!m) return false;
  const sessionID = m[1];
  const params = new URLSearchParams(location.search);
  const token = params.get('t') ?? '';

  document.body.classList.add('operator-mode');

  if (!token) {
    log('err', `operator mode: missing ?t=<token> in URL (session=${sessionID})`);
    setIdle(true, '缺少 user token');
    return true;
  }

  const wsProto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const httpBase = location.origin;
  const wsURL = `${wsProto}//${location.host}/ws/user`;

  log('info', `operator mode: session=${sessionID} base=${httpBase}`);
  setIdle(true, '正在连接…');

  try {
    await attachCloudSession(httpBase, wsURL, token);
  } catch (e) {
    log('err', `operator connect: ${toMsg(e)}`);
    setIdle(true, '连接失败');
  }
  return true;
}

// Initial idle state
setIdle(true, '尚未建立 WebRTC 连接');
updateLogMeta();

void bootstrapFromURL();

// ───────────────────────── Helpers ─────────────────────────

function must<T extends Element>(sel: string): T {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`missing element: ${sel}`);
  return el;
}

function toMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
